import {
  Button,
  column,
  Css,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  ModalBody,
  ModalFooter,
  ModalHeader,
  multiFilter,
  MultiSelectCardGroup,
  SelectCardGridGroupItemOption,
  simpleHeader,
  singleFilter,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MaterialType } from "src/generated/graphqlTypes";
import { useMaterialTypes } from "src/hooks/enums/useMaterialTypes";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";
import { StringParam, useQueryParams } from "use-query-params";
import {
  MergeMaterialVariants_MaterialVariantOptionFragment,
  useMergeMaterialVariants_MetadataQuery,
  useMergeMaterialVariants_SearchQuery,
} from "./MergeMaterialVariants.generated";

type SelectMaterialsModalProps = {
  selectedIds: string[];
  /** When set, only materials for this item can be selected. */
  lockedItemId?: string;
  onConfirm: (selectedIds: string[]) => void;
};

type SelectMaterialsFilter = { item?: string; brand?: string[]; type?: MaterialType; tag?: string[] };

type Row =
  | { kind: "header"; id: string; data: undefined }
  | { kind: "data"; id: string; data: MergeMaterialVariants_MaterialVariantOptionFragment[] };

export function SelectMaterialsModal(props: SelectMaterialsModalProps) {
  const { selectedIds, lockedItemId, onConfirm } = props;
  const { closeModal } = useModal();
  const [pendingIds, setPendingIds] = useState<string[]>(selectedIds);
  const [lockedType, setLockedType] = useState<MaterialType | undefined>();
  const [{ search: urlSearch }] = useQueryParams({ search: StringParam });

  const metadata = useMergeMaterialVariants_MetadataQuery();
  const types = useMaterialTypes();

  const itemLocked = !!lockedItemId || pendingIds.nonEmpty;
  const typeLocked = !!lockedType || pendingIds.nonEmpty;

  const filterDefs: FilterDefs<SelectMaterialsFilter> = useMemo(
    () => ({
      item: singleFilter({
        label: "Item",
        options: metadata.data?.items ?? [],
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
        defaultValue: lockedItemId,
        disabled: itemLocked ? "Same item required for merge" : undefined,
      }),
      brand: multiFilter({
        label: "Brand",
        options: metadata.data?.materialBrands ?? [],
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      type: singleFilter({
        label: "Type",
        options: types,
        getOptionValue: (o) => o.code,
        getOptionLabel: (o) => o.name,
        disabled: typeLocked ? "Same listing type required for merge" : undefined,
      }),
      tag: multiFilter({
        label: "Tag",
        options: metadata.data?.materialVariantTags ?? [],
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
    }),
    [metadata.data, types, itemLocked, typeLocked, lockedItemId],
  );

  // Ideally we would not be using this inside of a modal because it persists the filters to the url
  // but currently the GridTableLayout is the component that has been migrated over in beam to support the filtering UX that is needed for this design
  // There aren't any other conflicting components using the url for persistence, so this is fine for now for a temporary solution
  const layoutState = useGridTableLayoutState<SelectMaterialsFilter>({
    persistedFilter: { filterDefs, storageKey: "merge-select-materials" },
    search: "server",
  });
  const { filter, setFilter: setFilterRaw, searchString, setSearchString } = layoutState;

  // Beam seeds the search field from ?search= but only calls setSearchString on typing — sync so the query matches.
  useEffect(() => {
    if (urlSearch && urlSearch !== searchString) {
      setSearchString(urlSearch);
    }
  }, [urlSearch, searchString, setSearchString]);

  const setFilter = useCallback(
    (next: SelectMaterialsFilter) => {
      setFilterRaw({
        ...next,
        ...(itemLocked ? { item: filter.item ?? lockedItemId } : {}),
        ...(typeLocked ? { type: lockedType ?? filter.type } : {}),
      });
    },
    [setFilterRaw, itemLocked, typeLocked, filter.item, filter.type, lockedItemId, lockedType],
  );

  const clearFilters = useCallback(() => {
    setFilterRaw({
      ...(itemLocked ? { item: filter.item ?? lockedItemId } : {}),
      ...(typeLocked ? { type: lockedType ?? filter.type } : {}),
    });
    setSearchString(undefined);
  }, [setFilterRaw, itemLocked, typeLocked, filter.item, filter.type, lockedItemId, lockedType, setSearchString]);

  const lockedLayoutState = useMemo(
    () => ({ ...layoutState, setFilter, clearFilters }),
    [layoutState, setFilter, clearFilters],
  );

  const { data, loading } = useMergeMaterialVariants_SearchQuery({
    variables: {
      filter: {
        includeDeleted: true,
        ...(searchString ? { search: searchString } : {}),
        ...(filter.item ? { itemId: [filter.item] } : {}),
        ...(filter.brand?.nonEmpty ? { brandId: filter.brand } : {}),
        ...(filter.type ? { type: [filter.type] } : {}),
        ...(filter.tag?.nonEmpty ? { tags: filter.tag } : {}),
      },
      page: { limit: 50 },
    },
  });

  const variants = useMemo(() => data?.materialVariants.entities ?? [], [data?.materialVariants.entities]);
  const totalCount = data?.materialVariants.pageInfo.totalCount ?? 0;

  const toggleVariant = useCallback(
    (variant: MergeMaterialVariants_MaterialVariantOptionFragment) => {
      if (pendingIds.includes(variant.id)) {
        const next = pendingIds.filter((id) => id !== variant.id);
        setPendingIds(next);
        if (!lockedItemId && next.isEmpty) {
          setFilterRaw({ ...filter, item: undefined, type: undefined });
          setLockedType(undefined);
        }
        return;
      }
      const nextItemId = variant.listing.item.id;
      const nextType = variant.listing.type.code;
      const effectiveType = lockedType ?? filter.type;
      if (filter.item && nextItemId !== filter.item) return;
      if (effectiveType && nextType !== effectiveType) return;
      setLockedType((prev) => prev ?? nextType);
      setFilterRaw({ ...filter, item: filter.item ?? nextItemId, type: filter.type ?? nextType });
      setPendingIds([...pendingIds, variant.id]);
    },
    [pendingIds, lockedItemId, lockedType, filter, setFilterRaw],
  );

  // Bridge Beam's multi-select values → our per-variant toggle (item/type lock + filter sync).
  const onSelectionChange = useCallback(
    (nextVisibleIds: string[]) => {
      const visibleIds = variants.map((v) => v.id);
      const prevVisibleSelected = pendingIds.filter((id) => visibleIds.includes(id));
      const added = nextVisibleIds.find((id) => !prevVisibleSelected.includes(id));
      const removed = prevVisibleSelected.find((id) => !nextVisibleIds.includes(id));
      const toggledId = added ?? removed;
      if (!toggledId) return;
      const variant = variants.find((v) => v.id === toggledId);
      if (variant) toggleVariant(variant);
    },
    [variants, pendingIds, toggleVariant],
  );

  useEffect(() => {
    if (lockedType || pendingIds.isEmpty) return;
    const first = variants.find((v) => v.id === pendingIds[0]);
    if (!first) return;
    setLockedType(first.listing.type.code);
    setFilterRaw({
      ...filter,
      item: filter.item ?? first.listing.item.id,
      type: filter.type ?? first.listing.type.code,
    });
  }, [variants, pendingIds, lockedType, filter, setFilterRaw]);

  const rows: GridDataRow<Row>[] = useMemo(
    () =>
      variants.isEmpty ? [simpleHeader] : [simpleHeader, { kind: "data" as const, id: "materials", data: variants }],
    [variants],
  );

  const columns = useMemo(
    () =>
      createColumns({
        resultsLabel: loading ? "Loading…" : `${totalCount} results`,
        pendingIds,
        filterItemId: filter.item,
        lockedType,
        onSelectionChange,
      }),
    [loading, totalCount, pendingIds, filter.item, lockedType, onSelectionChange],
  );

  return (
    <>
      <ModalHeader>Select Materials</ModalHeader>
      <ModalBody>
        <div css={Css.maxhPx(560).oya.$} data-testid="selectMaterialsGrid">
          <GridTableLayout
            hideEditColumns
            layoutState={lockedLayoutState}
            emptyFallback={loading ? "Loading…" : "No materials found."}
            tableProps={{ rows, columns, style: { rowHeight: "flexible", allWhite: true, rowHover: false } }}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="quaternary" label="Cancel" onClick={closeModal} />
        <Button
          label="Select"
          disabled={pendingIds.length < 2}
          onClick={() => {
            onConfirm(pendingIds);
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

type CreateColumnsOpts = {
  resultsLabel: string;
  pendingIds: string[];
  filterItemId: string | undefined;
  lockedType: MaterialType | undefined;
  onSelectionChange: (nextIds: string[]) => void;
};

function createColumns(opts: CreateColumnsOpts): GridColumn<Row>[] {
  const { resultsLabel, pendingIds, filterItemId, lockedType, onSelectionChange } = opts;
  return [
    column<Row>({
      id: "material",
      name: "Material",
      header: () => (
        <span css={Css.sm.gray700.$} data-testid="selectMaterialsCount">
          {resultsLabel}
        </span>
      ),
      data: (variants) => ({
        content: (
          <MultiSelectCardGroup
            label="Materials"
            labelStyle="hidden"
            layout="horizontal"
            values={pendingIds}
            onChange={onSelectionChange}
            options={variants.map((variant) => {
              const selected = pendingIds.includes(variant.id);
              const wrongItem = !!filterItemId && variant.listing.item.id !== filterItemId;
              const wrongType = !!lockedType && variant.listing.type.code !== lockedType;
              const disabled = (wrongItem || wrongType) && !selected;
              return {
                value: variant.id,
                label: variant.displayName,
                description: variant.listing.item.name,
                image:
                  variant.featuredImage?.asset?.previewUrl ||
                  variant.featuredImage?.asset?.downloadUrl ||
                  PRODUCT_FALLBACK_IMG_URL,
                disabled,
                tooltip: disabled
                  ? wrongItem
                    ? "Same item required for merge"
                    : "Same listing type required for merge"
                  : undefined,
              } satisfies SelectCardGridGroupItemOption<string>;
            })}
          />
        ),
      }),
    }),
  ];
}
